import type { CostRow } from './cost-row';

/**
 * As colunas da tabela de custos. Cada uma sabe o próprio rótulo e como comparar
 * duas linhas por ela — então ordenar por qualquer coluna não é um `switch` no
 * componente, e acrescentar coluna é acrescentar uma constante aqui.
 *
 * A ordem desta lista é a ordem do cabeçalho.
 */
export class CostColumn {
  static readonly NAME = new CostColumn('nome', '', (row) => row.name.toLowerCase());
  static readonly CALLS = new CostColumn('chamadas', 'Chamadas', (row) => row.callsCount);
  static readonly AVERAGE = new CostColumn('media', 'Média/rodada', (row) => row.averageBrl);
  static readonly CACHE_HIT = new CostColumn('cache', 'Cache hit', (row) => row.cacheHitRate);
  static readonly MISS = new CostColumn('miss', 'Miss $', (row) => row.missUsd);
  static readonly HIT = new CostColumn('hit', 'Hit $', (row) => row.hitUsd);
  static readonly OUTPUT = new CostColumn('saida', 'Saída $', (row) => row.outputUsd);
  static readonly TOTAL = new CostColumn('total', 'Total', (row) => row.totalBrl);

  private static readonly BY_SLUG: Record<string, CostColumn> = {
    nome: CostColumn.NAME,
    chamadas: CostColumn.CALLS,
    media: CostColumn.AVERAGE,
    cache: CostColumn.CACHE_HIT,
    miss: CostColumn.MISS,
    hit: CostColumn.HIT,
    saida: CostColumn.OUTPUT,
    total: CostColumn.TOTAL,
  };

  private constructor(
    readonly slug: string,
    readonly label: string,
    private readonly valueOf: (row: CostRow) => number | string,
  ) {}

  /** Todas menos a primeira: o nome é o cabeçalho da visão, e o rótulo dele muda por aba. */
  static numeric(): CostColumn[] {
    return [
      CostColumn.CALLS,
      CostColumn.AVERAGE,
      CostColumn.CACHE_HIT,
      CostColumn.MISS,
      CostColumn.HIT,
      CostColumn.OUTPUT,
      CostColumn.TOTAL,
    ];
  }

  /** Slug desconhecido cai no total, que é a ordem de abertura da tabela. */
  static fromSlug(slug: string | null): CostColumn {
    return CostColumn.BY_SLUG[slug ?? ''] ?? CostColumn.TOTAL;
  }

  is(other: CostColumn): boolean {
    return this.slug === other.slug;
  }

  compare(left: CostRow, right: CostRow): number {
    const a = this.valueOf(left);
    const b = this.valueOf(right);
    if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
    return Number(a) - Number(b);
  }
}
